// src/pages/review/detail/DecisionPanel.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { decideApplication, selectActing } from '../../../redux/slices/underwriterSlice';
import { formatRupees } from '../../../utils/loanMath';
import { notify } from '../../../utils/notify';

const ACTIONS = [
  { key: 'APPROVE', label: 'Approve', tone: 'approve' },
  { key: 'APPROVE_WITH_CONDITIONS', label: 'Approve with conditions', tone: 'conditions' },
  { key: 'REQUEST_INFO', label: 'Request more information', tone: 'info' },
  { key: 'REJECT', label: 'Decline', tone: 'reject' }
];

const MIN_REMARKS = 10;

const DecisionPanel = ({ application, onRecorded }) => {
  const dispatch = useDispatch();
  const acting = useSelector(selectActing);

  const [action, setAction] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [approvedAmount, setApprovedAmount] = useState(application.loanAmount ?? '');
  const [conditionsText, setConditionsText] = useState('');
  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);

  const isApproval = action === 'APPROVE' || action === 'APPROVE_WITH_CONDITIONS';
  const isTerminal =
    action === 'APPROVE' || action === 'APPROVE_WITH_CONDITIONS' || action === 'REJECT';

  const validate = () => {
    const found = {};

    if (!action) found.action = 'Select a decision';

    if (remarks.trim().length < MIN_REMARKS) {
      found.remarks = `Explain your reasoning — at least ${MIN_REMARKS} characters`;
    }

    if (isApproval) {
      const amount = Number(approvedAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        found.approvedAmount = 'Enter a valid amount';
      } else if (amount > Number(application.loanAmount)) {
        found.approvedAmount = 'Cannot exceed the amount requested';
      }
    }

    if (action === 'APPROVE_WITH_CONDITIONS' && conditionsText.trim().length < MIN_REMARKS) {
      found.conditionsText = 'State the conditions the applicant must meet';
    }

    return found;
  };

  const handleReview = () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;
    setConfirming(true);
  };

  const handleConfirm = async () => {
    const payload = { action, remarks: remarks.trim() };
    if (isApproval) payload.approvedAmount = Number(approvedAmount);
    if (action === 'APPROVE_WITH_CONDITIONS') payload.conditionsText = conditionsText.trim();

    const result = await dispatch(decideApplication({ id: application.id, payload }));

    if (decideApplication.fulfilled.match(result)) {
      const messages = {
        APPROVE: 'Application approved',
        APPROVE_WITH_CONDITIONS: 'Approved with conditions',
        REJECT: 'Application declined',
        REQUEST_INFO: 'More information requested'
      };
      notify.success(messages[action] ?? 'Decision recorded');
      setConfirming(false);
      if (onRecorded) onRecorded();
    } else {
      notify.error(result.payload);
    }
  };

  // Second step. Approve and decline cannot be undone, so the underwriter
  // sees exactly what will be recorded before it is.
  if (confirming) {
    const chosen = ACTIONS.find((a) => a.key === action);

    return (
      <section className="decision-panel decision-panel--confirm">
        <h2 className="decision-panel__title">Confirm your decision</h2>

        <div className="confirm-summary">
          <div className="confirm-summary__row">
            <span className="confirm-summary__label">Decision</span>
            <span className={`confirm-summary__value confirm-summary__value--${chosen.tone}`}>
              {chosen.label}
            </span>
          </div>

          {isApproval && (
            <div className="confirm-summary__row">
              <span className="confirm-summary__label">Amount</span>
              <span className="confirm-summary__value numeric">{formatRupees(approvedAmount)}</span>
            </div>
          )}

          {conditionsText && (
            <div className="confirm-summary__block">
              <span className="confirm-summary__label">Conditions</span>
              <p className="confirm-summary__text">{conditionsText}</p>
            </div>
          )}

          <div className="confirm-summary__block">
            <span className="confirm-summary__label">Remarks</span>
            <p className="confirm-summary__text">{remarks}</p>
          </div>
        </div>

        {isTerminal && (
          <p className="decision-warning">
            This is final. Once recorded it cannot be changed or withdrawn, and the applicant will
            be notified.
          </p>
        )}

        <div className="decision-panel__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setConfirming(false)}
            disabled={acting}>
            Go back
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleConfirm}
            disabled={acting}>
            {acting ? 'Recording…' : 'Record decision'}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="decision-panel">
      <h2 className="decision-panel__title">Your decision</h2>

      <div className="decision-options" role="radiogroup" aria-label="Decision">
        {ACTIONS.map((option) => (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={action === option.key}
            className={`decision-option decision-option--${option.tone} ${
              action === option.key ? 'decision-option--selected' : ''
            }`}
            onClick={() => {
              setAction(option.key);
              setErrors((prev) => ({ ...prev, action: undefined }));
            }}>
            <span className="decision-option__marker" aria-hidden="true" />
            {option.label}
          </button>
        ))}
      </div>

      {errors.action && <span className="decision-error">{errors.action}</span>}

      {isApproval && (
        <div className="decision-field">
          <label className="decision-field__label" htmlFor="approvedAmount">
            Approved amount (₹)
          </label>
          <input
            id="approvedAmount"
            className={`control ${errors.approvedAmount ? 'control--error' : ''}`}
            inputMode="numeric"
            value={approvedAmount}
            onChange={(e) => {
              setApprovedAmount(e.target.value);
              setErrors((prev) => ({ ...prev, approvedAmount: undefined }));
            }}
          />
          <span className="decision-field__hint numeric">
            Requested: {formatRupees(application.loanAmount)}
          </span>
          {errors.approvedAmount && <span className="decision-error">{errors.approvedAmount}</span>}
        </div>
      )}

      {action === 'APPROVE_WITH_CONDITIONS' && (
        <div className="decision-field">
          <label className="decision-field__label" htmlFor="conditionsText">
            Conditions
          </label>
          <textarea
            id="conditionsText"
            className={`control control--area ${errors.conditionsText ? 'control--error' : ''}`}
            placeholder="What must the applicant do before disbursal?"
            value={conditionsText}
            onChange={(e) => {
              setConditionsText(e.target.value);
              setErrors((prev) => ({ ...prev, conditionsText: undefined }));
            }}
          />
          {errors.conditionsText && <span className="decision-error">{errors.conditionsText}</span>}
        </div>
      )}

      <div className="decision-field">
        <label className="decision-field__label" htmlFor="remarks">
          Reasoning
        </label>
        <textarea
          id="remarks"
          className={`control control--area ${errors.remarks ? 'control--error' : ''}`}
          placeholder="Record why you reached this decision. This is part of the audit trail."
          value={remarks}
          onChange={(e) => {
            setRemarks(e.target.value);
            setErrors((prev) => ({ ...prev, remarks: undefined }));
          }}
        />
        <span className="decision-field__hint">
          Shown to the applicant and kept in the permanent record.
        </span>
        {errors.remarks && <span className="decision-error">{errors.remarks}</span>}
      </div>

      <button
        type="button"
        className="btn btn--primary decision-panel__submit"
        onClick={handleReview}
        disabled={acting}>
        Review decision →
      </button>
    </section>
  );
};

DecisionPanel.propTypes = {
  application: PropTypes.shape({
    id: PropTypes.string.isRequired,
    loanAmount: PropTypes.string
  }).isRequired,
  onRecorded: PropTypes.func
};

export default DecisionPanel;
